import { parseBookingRequirements } from '../components/VenueDashboard/utils/bookingAddOns';
import { roundMoney } from './bookingPaymentFees';

const parseMoney = (value) => {
  if (value == null || value === '') return null;
  if (typeof value === 'number' && !Number.isNaN(value)) return value;
  const normalized = String(value).trim().toUpperCase();
  if (normalized === 'TBC' || normalized === 'N/A') return null;
  const parsed = Number.parseFloat(String(value).replace(/[^0-9.-]/g, ''));
  return Number.isFinite(parsed) ? parsed : null;
};

/** How venue waives charges on a booking. */
export const FREE_BOOKING_MODE = {
  NONE: 'none',
  SPACE_ONLY: 'space_only',
  ENTIRE: 'entire'
};

/** Keys left after accept/update wiped full requirements and only payment JSON remained. */
const PAYMENT_ONLY_REQUIREMENT_KEYS = new Set([
  'amount_paid',
  'amount_pending',
  'free_booking',
  'platform_fee',
  'processing_fee',
  'customer_info',
  'transaction_id',
  'payment_method',
  'add_ons_total',
  'packages',
  'last_priced_snapshot'
]);

/**
 * Detect requirements JSON replaced by free-booking normalization (payment fields + free_booking only).
 * These rows must not be treated as intentionally free.
 */
export const bookingHasCorruptedRequirements = (booking) => {
  if (!booking || typeof booking !== 'object') return false;
  const req = parseBookingRequirements(booking);
  if (!req || req.free_booking !== true) return false;

  const keys = Object.keys(req).filter((key) => {
    const value = req[key];
    if (value == null || value === '') return false;
    if (key === 'packages' && Array.isArray(value) && value.length === 0) return false;
    if (key === 'customer_info' && typeof value === 'object' && !Object.keys(value).length) {
      return false;
    }
    return true;
  });

  if (!keys.length) return false;
  return keys.every((key) => PAYMENT_ONLY_REQUIREMENT_KEYS.has(key));
};

export const formatFreeBookingModeLabel = (mode) => {
  switch (normalizeFreeBookingMode(mode)) {
    case FREE_BOOKING_MODE.SPACE_ONLY:
      return 'Free space (add-ons charged)';
    default:
      return 'Standard pricing';
  }
};

/** Entire-booking free is retired — treat it as space-only. */
export const normalizeFreeBookingMode = (mode) => {
  if (mode === FREE_BOOKING_MODE.SPACE_ONLY || mode === FREE_BOOKING_MODE.ENTIRE) {
    return FREE_BOOKING_MODE.SPACE_ONLY;
  }
  return FREE_BOOKING_MODE.NONE;
};

/** Read persisted free-booking mode from flags only — never infer from hire snapshots. */
export const resolveFreeBookingMode = (booking) => {
  if (!booking || typeof booking !== 'object') return FREE_BOOKING_MODE.NONE;

  const req = parseBookingRequirements(booking);
  const mode = String(
    req.free_mode ?? req.free_booking_mode ?? booking.free_mode ?? booking.free_booking_mode ?? ''
  ).toLowerCase();
  if (mode === FREE_BOOKING_MODE.NONE) {
    return FREE_BOOKING_MODE.NONE;
  }
  const markedSpaceFree =
    mode === FREE_BOOKING_MODE.SPACE_ONLY ||
    mode === FREE_BOOKING_MODE.ENTIRE ||
    req.space_free === true ||
    booking.space_free === true ||
    booking.free_booking === true ||
    booking.freeBooking === true ||
    req.free_booking === true;

  // Wiped requirements + leftover free_booking is not a real waiver.
  if (markedSpaceFree && bookingHasCorruptedRequirements(booking)) {
    return FREE_BOOKING_MODE.NONE;
  }
  if (markedSpaceFree) {
    return FREE_BOOKING_MODE.SPACE_ONLY;
  }
  return FREE_BOOKING_MODE.NONE;
};

export const isEntireFreeBooking = () => false;

export const isSpaceFreeBooking = (booking) =>
  resolveFreeBookingMode(booking) === FREE_BOOKING_MODE.SPACE_ONLY;

/**
 * Venue subtotal that the organiser is actually charged (before platform/processing fees).
 * Space-only mode waives the space fee but keeps add-ons (and applicable discount).
 */
export const resolveChargeableVenueSubtotal = ({
  baseAmount = 0,
  addOnsTotal = 0,
  discountAmount = 0,
  mode = FREE_BOOKING_MODE.NONE
} = {}) => {
  const base = roundMoney(baseAmount);
  const addons = roundMoney(addOnsTotal);
  const discount = roundMoney(discountAmount);
  const fullVenueTotal = roundMoney(Math.max(0, base + addons - discount));

  if (normalizeFreeBookingMode(mode) === FREE_BOOKING_MODE.SPACE_ONLY) {
    const addonDiscount = Math.min(discount, addons);
    return roundMoney(Math.max(0, addons - addonDiscount));
  }
  return fullVenueTotal;
};

export const resolveChargeableVenueSubtotalFromBooking = (booking, mode = resolveFreeBookingMode(booking)) => {
  if (!booking || typeof booking !== 'object') return 0;
  const req = parseBookingRequirements(booking);
  const pkgs = Array.isArray(req.packages)
    ? req.packages
    : Array.isArray(booking.packages)
      ? booking.packages
      : [];
  const storedAddOns = parseMoney(booking.add_ons_total ?? booking.addOnsTotal ?? req.add_ons_total);
  const packagedAddOns = pkgs.length
    ? roundMoney(
        pkgs.reduce((sum, line) => sum + (parseMoney(line.total ?? line.amount) ?? 0), 0)
      )
    : 0;
  const addOnsTotal =
    storedAddOns != null && storedAddOns > 0 ? storedAddOns : packagedAddOns;
  const baseAmount =
    parseMoney(req.base_fee) ??
    (parseMoney(req.booking_amount) != null && addOnsTotal > 0
      ? roundMoney(parseMoney(req.booking_amount) - addOnsTotal)
      : parseMoney(req.booking_amount)) ??
    0;
  const discountAmount = parseMoney(booking.discount_amount ?? req.discount_amount) ?? 0;

  return resolveChargeableVenueSubtotal({
    baseAmount,
    addOnsTotal,
    discountAmount,
    mode
  });
};

/** Requirements flags to persist for a free-booking mode. */
export const buildFreeBookingModeRequirements = (mode) => {
  if (normalizeFreeBookingMode(mode) === FREE_BOOKING_MODE.SPACE_ONLY) {
    return {
      free_mode: FREE_BOOKING_MODE.SPACE_ONLY,
      free_booking_mode: FREE_BOOKING_MODE.SPACE_ONLY,
      space_free: true
    };
  }
  return {
    free_mode: FREE_BOOKING_MODE.NONE,
    free_booking_mode: FREE_BOOKING_MODE.NONE,
    space_free: false
  };
};
